/**
 * Ledger double-entry — types + bất biến thuần (TDD §6.5/§7, ADR-0002).
 * Logic post/reversal đầy đủ sẽ hiện thực ở story S3.1/S3.3 (test-first theo TEST_STRATEGY §3).
 * File này định nghĩa hình dạng + invariant kiểm được mà không cần DB.
 */

export type AccountKind =
  | "CASH"
  | "MEMBER_RECEIVABLE"
  | "MEMBER_PAYABLE"
  | "INCOME"
  | "EXPENSE"
  | "LOAN_RECEIVABLE"
  | "EXTERNAL"
  // US-E18.F5-0198 (@UC9288): TIỀN CỌC đang treo ở bên thứ ba (villa/nhà xe). Là TÀI SẢN (quyền đòi
  // lại), KHÔNG PHẢI chi phí ⇒ đặt cọc DEBIT vào đây (không chạm EXPENSE); hoàn ⇒ CREDIT trả về
  // nguồn; mất ⇒ CREDIT và chỉ PHẦN MẤT chuyển sang EXPENSE.
  | "DEPOSIT_RECEIVABLE"
  // US-E18.F5-0198 (@UC9291): TÀI SẢN CHUNG đã mua (lều/loa) — nguyên giá CHƯA phân bổ vào chi phí.
  // Mua = DEBIT PREPAID_ASSET (không phải EXPENSE ngay); mỗi lần dùng = CREDIT PREPAID_ASSET /
  // DEBIT EXPENSE (khấu hao) ⇒ Σ khấu hao ≤ nguyên giá.
  | "PREPAID_ASSET"
  // 🔴🔴 US-E20.F2-0208 (@UC9327–@UC9330) — SỔ HIẾU HỈ HAI CHIỀU. **NGHĨA VỤ XÃ HỘI, KHÔNG PHẢI NỢ**:
  // "A mừng cưới B 500k ⇒ B nên đáp lễ khi A có việc" là ĐẠO LÝ, không phải khoản hệ thống đi đòi.
  //   RECIPROCAL_RECEIVABLE : mình ĐÃ đi lễ — giữ "quyền được đáp lễ"
  //   RECIPROCAL_PAYABLE    : mình ĐÃ nhận lễ — mang "nghĩa vụ đáp lễ"
  // 🔴 TÁCH BẠCH TUYỆT ĐỐI với MEMBER_RECEIVABLE/MEMBER_PAYABLE (nợ THẬT của quỹ): 2 kind này KHÔNG
  // vào `FinancialPositionService.hasNonZeroPosition`, KHÔNG sinh member_dues/debt_entries ⇒ người
  // còn "nợ đáp lễ" VẪN rời nhóm / xoá tài khoản BÌNH THƯỜNG (chặn oan mới là bug).
  // 🔴 KHÔNG bao giờ đi kèm chân CASH: cá nhân mừng cá nhân ⇒ quỹ KHÔNG mất/nhận đồng nào.
  | "RECIPROCAL_RECEIVABLE"
  | "RECIPROCAL_PAYABLE"
  // 🔴🔴 US-E20.F4-0210 (@UC9334–@UC9338) — TÀI SẢN ĐẦU TƯ BIẾN ĐỘNG GIÁ (crypto/CK/vàng). ADR 0006.
  // GIÁ VỐN (cost basis) của số ĐƠN VỊ đang nắm giữ — TÁCH TUYỆT ĐỐI KHỎI `CASH`:
  //   · mua : DEBIT ASSET_HOLDING / CREDIT CASH     (tiền rời két, đổi thành đơn vị tài sản)
  //   · bán : DEBIT CASH / CREDIT ASSET_HOLDING     (đúng GIÁ VỐN — hoàn vốn), lãi/lỗ ĐÃ THỰC HIỆN
  //           đi ở BÚT TOÁN RIÊNG (OTHER_INCOME / EXPENSE).
  // 🔴 GIÁ THỊ TRƯỜNG LÊN/XUỐNG ⇒ **0 BÚT TOÁN**: số dư ASSET_HOLDING LUÔN là GIÁ VỐN, KHÔNG BAO GIỜ
  // được "định giá lại" bằng bút toán. Lãi/lỗ CHƯA thực hiện là VIEW (@goppy/domain `markToMarket`)
  // — ghi nó vào sổ là đẻ tiền không có thật, và phá luôn khả năng đối chiếu sổ ↔ tiền thật.
  | "ASSET_HOLDING";

/**
 * Nguồn sự thật DUY NHẤT của `txType` (US-INFRA.F2-0163 S01, @UC9178).
 *
 * DB có CHECK `ledger_transactions_txtype_check` với đúng danh sách này. Kiểu `TxType` được
 * SUY RA từ mảng ⇒ thêm giá trị mới mà quên migration sẽ làm đỏ test drift
 * (`ledger-txtype-migration-sync.test.ts`), thay vì lọt xuống DB rồi hỏng âm thầm.
 */
export const TX_TYPES = [
  "CONTRIBUTION",
  "EXPENSE",
  // US-E16.F5-0186 (@UC9248): 3 loại dưới đây TỪNG CHẾT (khai enum + CHECK DB + filter đọc nhưng 0
  // producer). GIỜ CÓ PRODUCER THẬT: `RecordIncomeService` (apps/api/src/modules/ledger/
  // record-income.service.ts — POST /v1/funds/:id/income) post DEBIT CASH / CREDIT INCOME.
  // Lưới chống tái diễn: apps/api/src/modules/ledger/txtype-producer-coverage.spec.ts (mọi txType
  // trừ REVERSAL phải có ≥1 post-site THẬT trong apps/api/src).
  "SPONSORSHIP_MEMBER",
  "SPONSORSHIP_EXTERNAL",
  "OTHER_INCOME",
  "LOAN_OUT",
  "LOAN_REPAYMENT",
  "SETTLEMENT",
  "PAY_ON_BEHALF",
  "OFFSET",
  "REVERSAL",
  // US-E15.F3-0178 (@UC9217): producer advance_credit — quỹ nhận tiền member ứng trước
  // (DEBIT CASH / CREDIT MEMBER_PAYABLE). Thêm additive vào CHECK migration mới (sync test đọc
  // migration là NGUỒN SỰ THẬT hiện hành). KHÔNG xoá txType cũ.
  "ADVANCE_CREDIT",
  // US-E15.F4-0179 (@UC9220): cascade + partial refund — NCC hoàn tiền chảy ngược cho người ĐÃ GÁNH
  // (peer-internal balanced: DEBIT MEMBER_PAYABLE(debtor) / CREDIT MEMBER_RECEIVABLE(creditor), mirror
  // ngược SETTLEMENT). Thêm additive vào CHECK migration mới (sync test đọc migration mới nhất).
  "REFUND",
  // US-E18.F1-0194 (@UC9271): CHUYỂN TIỀN LIÊN SỔ (inter-fund transfer) — 2 chân, mỗi chân là 1 tx
  // RIÊNG (LedgerTransaction.fundId là single-fund ⇒ không thể 1 tx bắc cầu 2 sổ), post ATOMIC cùng
  // 1 transaction, link qua `linkedTxId` (trỏ nhau):
  //   FUND_TRANSFER_OUT (sổ nguồn): DEBIT EXTERNAL / CREDIT CASH  ⇒ CASH −N
  //   FUND_TRANSFER_IN  (sổ đích):  DEBIT CASH     / CREDIT EXTERNAL ⇒ CASH +N
  // 🔴 Σ CASH toàn nhóm BẤT BIẾN (−N + N = 0): tiền chỉ DỜI sổ, KHÔNG sinh/mất.
  // Producer: apps/api/src/modules/ledger/inter-fund-transfer.service.ts.
  "FUND_TRANSFER_OUT",
  "FUND_TRANSFER_IN",
  // US-E18.F2-0195 (@UC9276): ĐỔI HÌNH THÁI QUỸ (value form) — CÙNG SỔ, 2 chân CÙNG kind=CASH nhưng
  // KHÁC `valueForm` (kênh nắm giữ): CREDIT CASH@<from> / DEBIT CASH@<to> (vd rút mặt từ bank).
  // 🔴 Σ CASH TOÀN SỔ BẤT BIẾN (−N + N = 0): tiền chỉ ĐỔI KÊNH — KHÔNG thu, KHÔNG chi, KHÔNG sinh/mất.
  // Sự kiện TRUNG TÍNH ⇒ read-surface (fund-dashboard/transactions) CỐ Ý loại trừ khỏi thu/chi.
  // Producer: apps/api/src/modules/ledger/value-form.service.ts.
  "VALUE_FORM_SWITCH",
  // US-E18.F5-0198 (@UC9288–@UC9291) — CỌC + KHẤU HAO. 🔴 Cọc/tài sản = LUÂN CHUYỂN/TÀI SẢN, KHÔNG
  // phải chi phí ⇒ cần txType RIÊNG để read-surface KHÔNG đếm chúng là thu/chi:
  //   DEPOSIT_PLACE   : DEBIT DEPOSIT_RECEIVABLE / CREDIT CASH|MEMBER_PAYABLE  (0 chi phí)
  //   DEPOSIT_REFUND  : DEBIT CASH|MEMBER_PAYABLE / CREDIT DEPOSIT_RECEIVABLE  (0 chi phí — hoàn đủ)
  //   DEPOSIT_FORFEIT : DEBIT EXPENSE(mất) + DEBIT CASH|MEMBER_PAYABLE(còn) / CREDIT DEPOSIT_RECEIVABLE
  //                     (CHỈ phần mất là chi phí chung)
  //   ASSET_PURCHASE  : DEBIT PREPAID_ASSET / CREDIT CASH                      (0 chi phí lúc mua)
  //   DEPRECIATION    : DEBIT EXPENSE / CREDIT PREPAID_ASSET                   (chi phí sinh dần, Σ ≤ nguyên giá)
  // Producer: apps/api/src/modules/ledger/deposit.service.ts + shared-asset.service.ts.
  "DEPOSIT_PLACE",
  "DEPOSIT_REFUND",
  "DEPOSIT_FORFEIT",
  "ASSET_PURCHASE",
  "DEPRECIATION",
  // 🔴🔴 US-E20.F1-0207 (@UC9321–@UC9325) — HỤI/HỌ (ROSCA). Sổ hụi là POOL tổng-bằng-không:
  //   HUI_CONTRIB    : DEBIT CASH / CREDIT MEMBER_RECEIVABLE(người đóng)   ⇒ tiền VÀO pool
  //   HUI_COMMISSION : DEBIT MEMBER_RECEIVABLE(người hốt) / CREDIT MEMBER_PAYABLE(chủ hụi)
  //                    ⇒ hoa hồng = BÚT TOÁN RIÊNG, 0 chân CASH (không lẫn payout — P1.2)
  //   HUI_TAKE       : DEBIT MEMBER_RECEIVABLE(người hốt) / CREDIT CASH    ⇒ tiền RA pool (net)
  // 🔴 Σ VÀO = Σ RA mỗi kỳ: Σ đóng (gross) = net người hốt + hoa hồng. Σ MEMBER_RECEIVABLE toàn dây
  // = 0 (trò chơi tổng-bằng-không) — người ĐÃ hốt VẪN nợ pool cho tới khi đóng hết các kỳ sau.
  // Producer: apps/api/src/modules/hui/hui.service.ts.
  "HUI_CONTRIB",
  "HUI_TAKE",
  "HUI_COMMISSION",
  // 🔴🔴 US-E20.F2-0208 (@UC9327–@UC9330) — SỔ HIẾU HỈ (nghĩa vụ XÃ HỘI, KHÔNG phải nợ):
  //   GIFT_GIVEN    : DEBIT RECIPROCAL_RECEIVABLE(người đi lễ) / CREDIT RECIPROCAL_PAYABLE(người nhận)
  //   GIFT_RECEIVED : chiều ngược lại
  // 🔴 CẢ HAI: 0 CHÂN CASH ⇒ Σ CASH của sổ BẤT BIẾN khi cá nhân mừng cá nhân (tiền túi, không qua
  // quỹ). Khi QUỸ đứng ra mừng/nhận, dòng tiền THẬT đi ở BÚT TOÁN RIÊNG bằng txType SẴN CÓ (EXPENSE
  // / OTHER_INCOME) — KHÔNG trộn chân CASH vào GIFT_* (P1.2 không dòng lai).
  // 🔴 TRUNG TÍNH ở read-surface: GIFT_* KHÔNG phải thu/chi của quỹ ⇒ transactions.service liệt vào
  // NEUTRAL_TX_TYPES (nếu để rơi vào nhánh else sẽ hiện "CHI 500.000đ" trong khi quỹ không mất đồng
  // nào = NÓI DỐI người dùng — đúng lớp bug VALUE_FORM_SWITCH @UC9276).
  // Producer: apps/api/src/modules/reciprocal/reciprocal.service.ts.
  "GIFT_GIVEN",
  "GIFT_RECEIVED",
  // 🔴🔴 US-E20.F4-0210 (@UC9334–@UC9338) — ĐẦU TƯ NAV (ADR 0006). HAI txType, CẢ HAI ĐỀU LÀ
  // **ĐỔI HÌNH THÁI GIÁ TRỊ** (CASH ↔ ASSET_HOLDING), KHÔNG phải thu/chi:
  //   INVEST_BUY  : DEBIT ASSET_HOLDING(giá vốn) / CREDIT CASH(tiền mua)  ⇒ CASH −N, holding +N
  //   INVEST_SELL : DEBIT CASH(GIÁ VỐN phần bán) / CREDIT ASSET_HOLDING   ⇒ hoàn vốn, holding −giá vốn
  // 🔴 LÃI/LỖ ĐÃ THỰC HIỆN của lần bán đi ở **BÚT TOÁN RIÊNG** bằng txType SẴN CÓ (KHÔNG trộn vốn với
  // lãi — P1.2 không dòng lai): lãi ⇒ OTHER_INCOME (DEBIT CASH / CREDIT INCOME); lỗ ⇒ EXPENSE
  // (DEBIT EXPENSE / CREDIT CASH). ⇒ Σ CASH sau bán = giá vốn + lãi (hoặc − lỗ) = ĐÚNG tiền thu về.
  // 🔴 TRUNG TÍNH ở read-surface: INVEST_BUY/INVEST_SELL vào `NEUTRAL_TX_TYPES` (transactions.service)
  // — giá trị ròng của quỹ KHÔNG đổi khi đổi tiền lấy tài sản; để rơi vào nhánh else sẽ hiện
  // "CHI 10.000.000đ" trong khi quỹ không nghèo đi đồng nào = NÓI DỐI (đúng lớp bug VALUE_FORM_SWITCH
  // @UC9276 / GIFT_* @UC9330). Lãi/lỗ THẬT vẫn hiện đầy đủ qua OTHER_INCOME/EXPENSE.
  // 🔴 KHÔNG có txType nào cho "định giá lại": giá lên/xuống ⇒ 0 bút toán (bất biến số một).
  // Producer: apps/api/src/modules/invest/invest.service.ts.
  "INVEST_BUY",
  "INVEST_SELL",
] as const;

export type TxType = (typeof TX_TYPES)[number];

/** Nguồn sự thật của `side` — DB có CHECK `ledger_lines_side_check`. */
export const SIDES = ["DEBIT", "CREDIT"] as const;

export type Side = (typeof SIDES)[number];

export interface LedgerLineDraft {
  readonly accountId: string;
  readonly side: Side;
  readonly amountMinor: bigint; // > 0
  readonly currency: string;
}

export interface LedgerTransactionDraft {
  readonly fundId: string;
  readonly txType: TxType;
  readonly lines: readonly LedgerLineDraft[];
  readonly idempotencyKey?: string;
}

/**
 * Bất biến double-entry: Σ(DEBIT) = Σ(CREDIT), mỗi line amount > 0, ≥ 2 lines.
 * Trả về true nếu cân bằng; ném lỗi nếu vi phạm (để service từ chối trước khi chạm DB).
 */
export function assertBalanced(tx: LedgerTransactionDraft): true {
  if (tx.lines.length < 2) {
    throw new Error(`[ledger] transaction cần ≥ 2 lines (có ${tx.lines.length})`);
  }
  let debit = 0n;
  let credit = 0n;
  for (const line of tx.lines) {
    if (line.amountMinor <= 0n) {
      throw new Error(`[ledger] amountMinor phải > 0 (account ${line.accountId})`);
    }
    if (line.side === "DEBIT") debit += line.amountMinor;
    else credit += line.amountMinor;
  }
  if (debit !== credit) {
    throw new Error(`[ledger] mất cân bằng: debit=${debit} credit=${credit}`);
  }
  return true;
}

/**
 * Đảo toàn bộ transaction gốc thành REVERSAL: mỗi line đổi DEBIT↔CREDIT, amount giữ nguyên.
 * Gốc bất biến (append-only); reversal là transaction MỚI (TDD §7.4).
 */
export function buildReversal(original: LedgerTransactionDraft): LedgerTransactionDraft {
  return {
    fundId: original.fundId,
    txType: "REVERSAL",
    lines: original.lines.map((l) => ({
      ...l,
      side: l.side === "DEBIT" ? "CREDIT" : "DEBIT",
    })),
  };
}

/**
 * 🔴 PHÂN LOẠI THU/CHI Ở PHẠM VI **BÁO CÁO NHÓM** — nguồn sự thật DUY NHẤT (US-AI-0302).
 *
 * 🔴🔴 ĐỌC KỸ TRƯỚC KHI "DRY CHO GỌN": repo có **HAI** khái niệm thu/chi, cố ý khác nhau.
 *
 *  (1) **BÁO CÁO NHÓM** (bảng dưới đây) — dùng cho tổng thu/chi kỳ, biểu đồ, CSV, và câu trả lời
 *      của trợ lý. CỐ Ý **KHÔNG** gồm `FUND_TRANSFER_IN/OUT` và `VALUE_FORM_SWITCH`: chuyển giữa hai
 *      sổ của CÙNG nhóm, hay đổi hình thái tiền mặt↔bank, **không phải tiền mới vào** cũng **không
 *      phải tiền rời nhóm**. Cộng vào là **thổi phồng báo cáo** (số ảo, double-count).
 *
 *  (2) **XEM MỘT SỔ** (`transactions.service.ts`) — mũi tên thu/chi THEO SỔ ĐANG XEM, nên
 *      `FUND_TRANSFER_IN` LÀ "thu" của sổ đó. Đúng cho view một sổ, SAI cho báo cáo nhóm.
 *
 * ⇒ Gộp hai tập này làm một là tạo ra bug tiền. Nếu cần dùng, hãy chọn ĐÚNG phạm vi.
 *
 * VÌ SAO ĐƯA VỀ ĐÂY: audit 22/07 đo được **3 bản chép tay** của tập báo cáo
 * (`fund-dashboard.service.ts`, `home-summary.service.ts`, `report-csv.ts`) và trợ lý AI thì dùng
 * một tập **THỨ TƯ, THIẾU 4 LOẠI** (chỉ `CONTRIBUTION`) ⇒ hỏi dashboard và hỏi trợ lý ra **hai con số
 * tổng thu khác nhau cho cùng một tháng**. Không ai sai cú pháp; drift lặng lẽ giữa các bản chép.
 */
export const REPORT_INCOME_TX_TYPES: ReadonlySet<string> = new Set([
  "CONTRIBUTION",
  "SPONSORSHIP_MEMBER",
  "SPONSORSHIP_EXTERNAL",
  "OTHER_INCOME",
  "LOAN_REPAYMENT",
]);

/** Chi ở phạm vi BÁO CÁO NHÓM. Xem docblock `REPORT_INCOME_TX_TYPES` về hai phạm vi. */
export const REPORT_EXPENSE_TX_TYPES: ReadonlySet<string> = new Set(["EXPENSE", "LOAN_OUT"]);

/**
 * 🔴 US-INFRA-0301 lớp 5 — PHẠM VI THỨ HAI: **XEM MỘT SỔ** (`transactions.service.ts`).
 *
 * Khác `REPORT_INCOME_TX_TYPES` đúng MỘT phần tử: `FUND_TRANSFER_IN`. Chuyển từ sổ khác sang **LÀ
 * tiền vào sổ đang xem** ⇒ mũi tên "thu" của sổ đó là ĐÚNG. Nhưng ở phạm vi BÁO CÁO NHÓM thì không —
 * tiền chỉ dời chỗ trong cùng nhóm, cộng vào là thổi phồng.
 *
 * ⚠️ **KHÔNG gộp hai tập này.** Một phần tử khác biệt đó chính là ranh giới giữa "đúng" và "số ảo".
 * Khai riêng ở đây, cạnh nhau, để người sửa nhìn thấy CẢ HAI và biết mình đang đổi phạm vi nào.
 */
export const LEDGER_VIEW_INCOME_TX_TYPES: ReadonlySet<string> = new Set([
  ...REPORT_INCOME_TX_TYPES,
  "FUND_TRANSFER_IN",
]);

/**
 * 🔴 PHẠM VI THỨ BA: txType **TRUNG TÍNH** — không phải thu, cũng không phải chi, ở MỌI phạm vi.
 *
 * `VALUE_FORM_SWITCH` (đổi hình thái tiền mặt↔bank trong cùng sổ) và các bút toán hiếu hỉ / đầu tư:
 * Σ CASH của sổ **bất biến** hoặc chuyển động không mang nghĩa thu-chi nhóm. Lọt vào danh sách thu
 * hay chi đều làm sai báo cáo.
 */
export const NEUTRAL_TX_TYPES: ReadonlySet<string> = new Set([
  "VALUE_FORM_SWITCH",
  "GIFT_GIVEN",
  "GIFT_RECEIVED",
  "INVEST_BUY",
  "INVEST_SELL",
]);

/**
 * 🔴 PHẠM VI THỨ TƯ: **FEED HOẠT ĐỘNG NHÓM — "ai đó ĐÃ NỘP QUỸ"** (US-E24.F12-0361 @UC9591).
 * Dùng bởi `apps/api/src/modules/engagement/activity-feed.service.ts`.
 *
 * 🔴🔴 VÌ SAO KHÔNG DÙNG LẠI `REPORT_INCOME_TX_TYPES` — đây là tập **HẸP HƠN THẬT SỰ**, không phải
 * bản chép thiếu. Feed trả lời câu *"X đã nộp quỹ"*, nên chỉ nhận txType vừa (a) có chân **DEBIT
 * CASH** (tiền thật vào quỹ) vừa (b) gắn `memberDueId` (nghĩa vụ của MỘT người được hoàn thành):
 *  - `CONTRIBUTION`  — 3 đường xác nhận: two_party (`approve-decline.service.ts`), admin_one_party
 *                      (`record-paid.service.ts`), gateway_auto (`gateway-webhook.service.ts`)
 *  - `PAY_ON_BEHALF` — đường thứ 4 (`pay-on-behalf.service.ts`): người khác nộp thay, tiền VẪN vào
 *                      quỹ. Bỏ sót loại này = bỏ sót đúng một đường xác nhận.
 *
 * 🔴 Tập này **KHÔNG phải tập con** của `REPORT_INCOME_TX_TYPES` — hai chiều đều khác, đo thật:
 *  - `REPORT_INCOME \ FEED` = **4**: `SPONSORSHIP_MEMBER`, `SPONSORSHIP_EXTERNAL`, `OTHER_INCOME`,
 *    `LOAN_REPAYMENT`. Đều là tiền vào quỹ THẬT (nên ĐÚNG ở phạm vi báo cáo) nhưng **không ai
 *    "hoàn thành nghĩa vụ đợt thu"** cả ⇒ dựng thành dòng *"X đã nộp quỹ"* là **nói sai chuyện đã
 *    xảy ra**. Cộng vào báo cáo là đúng; cộng vào feed là bịa một sự kiện không có.
 *  - `FEED \ REPORT_INCOME` = **1**: `PAY_ON_BEHALF`. Nó có mặt ở feed vì tiền THẬT SỰ vào quỹ và
 *    một nghĩa vụ THẬT SỰ được hoàn thành. Việc nó vắng ở tập báo cáo là trạng-thái-sẵn-có của repo,
 *    **không** phải thứ task này thay đổi — đừng "sửa cho đối xứng" mà không đọc lại phạm vi báo cáo.
 *
 * Khác `LEDGER_VIEW_INCOME_TX_TYPES` thêm `FUND_TRANSFER_IN`: chuyển giữa hai sổ của CÙNG nhóm là
 * "thu" khi xem riêng sổ đích, nhưng **không ai nộp gì cả** ⇒ **KHÔNG BAO GIỜ** vào feed.
 *
 * `REVERSAL` KHÔNG có ở đây ⇒ bút toán đảo không tự đẻ item; bút toán gốc ĐÃ BỊ ĐẢO bị loại bằng
 * `reversedBy: { none: {} }` ở tầng query (hiện "đã nộp 200k" cho khoản đã undo-record = nói dối
 * về tiền).
 *
 * ⚠️ Khai ở đây — cạnh 3 phạm vi kia — để người thêm `txType` mới nhìn thấy **cả bốn** và biết mình
 * đang đổi phạm vi nào. Guard `concept-drift.invariants.spec.ts` cấm chép tập này ra nơi khác.
 */
export const FEED_MONEY_IN_TX_TYPES: readonly string[] = ["CONTRIBUTION", "PAY_ON_BEHALF"];
